import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

export interface Customer {
  id: string;
  name: string;
  phone: string;
  email: string;
  segment: string;
  tags: string[];
  group: string;
  lastContact: string;
  totalDeals: number;
}

export interface CustomerGroup {
  id: string;
  name: string;
  description: string | null;
  count: number;
}

interface DbCustomer {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  segment: string | null;
  tags: string[] | null;
  group_name: string | null;
  last_contact: string | null;
  total_deals: number | null;
}

interface DbGroup {
  id: string;
  name: string;
  description: string | null;
}

function mapCustomer(row: DbCustomer): Customer {
  return {
    id: row.id,
    name: row.name,
    phone: row.phone || '',
    email: row.email || '',
    segment: row.segment || 'FİZBO',
    tags: Array.isArray(row.tags) ? row.tags : [],
    group: row.group_name || 'Genel',
    lastContact: row.last_contact || '',
    totalDeals: row.total_deals || 0,
  };
}

function mapGroup(row: DbGroup): CustomerGroup {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    count: 0,
  };
}

export function useCustomers() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [groups, setGroups] = useState<CustomerGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    setError(null);

    const [cRes, gRes] = await Promise.all([
      supabase.from('ep_customers').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
      supabase.from('ep_customer_groups').select('*').eq('user_id', user.id).order('name', { ascending: true }),
    ]);

    if (cRes.error || gRes.error) {
      setError(cRes.error?.message || gRes.error?.message || 'Veri yüklenirken hata oluştu');
      setIsLoading(false);
      return;
    }

    const customersData = (cRes.data || []).map(mapCustomer);
    const groupsData = (gRes.data || []).map(mapGroup);

    const groupCounts = customersData.reduce<Record<string, number>>((acc, c) => {
      const g = c.group || 'Genel';
      acc[g] = (acc[g] || 0) + 1;
      return acc;
    }, {});

    const enrichedGroups = groupsData.map((g) => ({
      ...g,
      count: groupCounts[g.name] || 0,
    }));

    setCustomers(customersData);
    setGroups(enrichedGroups);
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const addCustomer = useCallback(
    async (form: {
      name: string;
      phone: string;
      email: string;
      segment: string;
      group: string;
      tags?: string[];
    }): Promise<{ error: string | null; customer?: Customer }> => {
      if (!user) return { error: 'Oturum açmalısınız.' };

      const dbRow = {
        user_id: user.id,
        name: form.name,
        phone: form.phone,
        email: form.email,
        segment: form.segment,
        group_name: form.group,
        tags: form.tags || [],
        total_deals: 0,
      };

      const { data, error: dbError } = await supabase
        .from('ep_customers')
        .insert(dbRow)
        .select()
        .single();

      if (dbError || !data) {
        return { error: dbError?.message || 'Ekleme başarısız oldu.' };
      }

      const newCustomer = mapCustomer(data as DbCustomer);
      setCustomers((prev) => [newCustomer, ...prev]);
      setGroups((prev) =>
        prev.map((g) => (g.name === newCustomer.group ? { ...g, count: g.count + 1 } : g))
      );
      return { error: null, customer: newCustomer };
    },
    [user]
  );

  const updateCustomer = useCallback(
    async (id: string, updates: Partial<Customer>): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Oturum açmalısınız.' };

      const dbUpdates: Record<string, unknown> = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.phone !== undefined) dbUpdates.phone = updates.phone;
      if (updates.email !== undefined) dbUpdates.email = updates.email;
      if (updates.segment !== undefined) dbUpdates.segment = updates.segment;
      if (updates.group !== undefined) dbUpdates.group_name = updates.group;
      if (updates.tags !== undefined) dbUpdates.tags = updates.tags;
      if (updates.totalDeals !== undefined) dbUpdates.total_deals = updates.totalDeals;
      if (updates.lastContact !== undefined) dbUpdates.last_contact = updates.lastContact;

      const { error: dbError } = await supabase
        .from('ep_customers')
        .update(dbUpdates)
        .eq('id', id)
        .eq('user_id', user.id);

      if (dbError) {
        return { error: dbError.message };
      }

      const old = customers.find((c) => c.id === id);
      setCustomers((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c)));

      if (old && updates.group && old.group !== updates.group) {
        setGroups((prev) =>
          prev.map((g) => {
            if (g.name === old.group) return { ...g, count: Math.max(0, g.count - 1) };
            if (g.name === updates.group) return { ...g, count: g.count + 1 };
            return g;
          })
        );
      }
      return { error: null };
    },
    [user, customers]
  );

  const deleteCustomer = useCallback(
    async (id: string): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Oturum açmalısınız.' };

      const { error: dbError } = await supabase
        .from('ep_customers')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (dbError) {
        return { error: dbError.message };
      }

      const removed = customers.find((c) => c.id === id);
      setCustomers((prev) => prev.filter((c) => c.id !== id));
      if (removed) {
        setGroups((prev) =>
          prev.map((g) => (g.name === removed.group ? { ...g, count: Math.max(0, g.count - 1) } : g))
        );
      }
      return { error: null };
    },
    [user, customers]
  );

  return {
    customers,
    groups,
    isLoading,
    error,
    refresh: fetchAll,
    addCustomer,
    updateCustomer,
    deleteCustomer,
  };
}